import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardPreview,
  Text,
  Spinner,
  MessageBar,
  Textarea,
} from "@fluentui/react-components";
import { ArrowLeftRegular, Save24Regular } from "@fluentui/react-icons";
import {
  useCascoConditions,
  useUpdateInsurerCascoConditions,
} from "../../../state/queries/useCascoConditions";
import { InsurerCondition } from "../../../library/types/cascoConditions";
import { Navigation } from "../../../components/Navigation/Navigation";
import { useHasAnyRole } from "../../../hooks/useUserRoles";
import { RolePermissions } from "../../../library/constants/roles";
import { ConfirmDialog } from "../../../components/ConfirmDialog";

export const InsurerEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { insurerName } = useParams<{ insurerName: string }>();
  const [editingConditions, setEditingConditions] = useState<{
    [key: number]: string;
  }>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  // Check if user can edit settings (only admins)
  const canEditSettings = useHasAnyRole(RolePermissions.canEditSettings());

  const { data: conditions, isLoading, error } = useCascoConditions();
  const updateInsurerMutation = useUpdateInsurerCascoConditions();

  // Get all conditions for this insurer
  const insurerConditions = useMemo(() => {
    if (!conditions || !insurerName) return [];

    const result: Array<{
      CascoConditionId: number;
      CascoConditionInsurerId: number;
      ConditionName: string;
      InsurerText: string;
    }> = [];

    conditions.forEach((condition) => {
      const insurerCondition = condition.InsurerConditions.find(
        (ic) => ic.InsurerName === insurerName
      );

      if (insurerCondition) {
        result.push({
          CascoConditionId: condition.CascoConditionId,
          CascoConditionInsurerId: insurerCondition.CascoConditionInsurerId,
          ConditionName: condition.Name,
          InsurerText: insurerCondition.InsurerText,
        });
      }
    });

    return result;
  }, [conditions, insurerName]);

  useEffect(() => {
    if (insurerConditions.length > 0) {
      // Initialize editing state with current condition texts
      const initialEditingState: { [key: number]: string } = {};
      insurerConditions.forEach((ic) => {
        initialEditingState[ic.CascoConditionInsurerId] = ic.InsurerText;
      });
      setEditingConditions(initialEditingState);
    }
  }, [insurerConditions]);

  const handleConditionTextChange = (
    cascoConditionInsurerId: number,
    newText: string
  ) => {
    setEditingConditions((prev) => ({
      ...prev,
      [cascoConditionInsurerId]: newText,
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!insurerName) return;

    try {
      const updatedInsurerConditions: InsurerCondition[] =
        insurerConditions.map((ic) => ({
          CascoConditionInsurerId: ic.CascoConditionInsurerId,
          CascoConditionId: ic.CascoConditionId,
          InsurerName: insurerName,
          InsurerText:
            editingConditions[ic.CascoConditionInsurerId] || ic.InsurerText,
        }));

      await updateInsurerMutation.mutateAsync(updatedInsurerConditions);
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      navigate("/settings/casco-conditions");
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    navigate("/settings/casco-conditions");
  };

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <Spinner label={t("qtg.common.loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "1rem" }}>
        <MessageBar intent="error">
          {t("qtg.settings.cascoConditions.loadError")}
        </MessageBar>
      </div>
    );
  }

  if (!insurerName) {
    return (
      <div style={{ padding: "1rem" }}>
        <MessageBar intent="warning">
          {t(
            "qtg.settings.cascoConditions.insurerNotFound",
            "Insurer not found."
          )}
        </MessageBar>
      </div>
    );
  }

  return (
    <div>
      <Navigation
        displaySearch={false}
        displayReload={false}
        rightSideButtons={
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              appearance="secondary"
              icon={<ArrowLeftRegular />}
              onClick={handleBack}
            >
              {t("qtg.common.back")}
            </Button>
            {canEditSettings && (
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleSaveChanges}
                disabled={!hasChanges || updateInsurerMutation.isPending}
              >
                {updateInsurerMutation.isPending
                  ? t("qtg.common.saving")
                  : t("qtg.common.save")}
              </Button>
            )}
          </div>
        }
      />

      <div style={{ padding: "1rem" }}>
        {!canEditSettings && (
          <MessageBar intent="info" style={{ marginBottom: "1rem" }}>
            {t(
              "qtg.settings.cascoConditions.readOnlyAccess",
              "You have read-only access to settings. Contact an administrator to make changes."
            )}
          </MessageBar>
        )}
        <Card>
          <CardHeader
            header={
              <div>
                <Text weight="semibold" size={500}>
                  {t(
                    "qtg.settings.cascoConditions.editInsurerConditions",
                    "Edit Insurer Conditions"
                  )}
                </Text>
                <br />
                <Text
                  size={400}
                  style={{ color: "var(--colorNeutralForeground2)" }}
                >
                  {insurerName}
                </Text>
              </div>
            }
          />
          <CardPreview style={{ padding: 0 }}>
            {hasChanges && canEditSettings && (
              <div style={{ padding: "16px 16px 0 16px" }}>
                <MessageBar intent="info">
                  {t("qtg.settings.cascoConditions.unsavedChanges")}
                </MessageBar>
              </div>
            )}

            <div style={{ width: "100%" }}>
              {insurerConditions.length === 0 ? (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <Text>
                    {t(
                      "qtg.settings.cascoConditions.noConditionsForInsurer",
                      "No conditions configured for this insurer."
                    )}
                  </Text>
                </div>
              ) : (
                <>
                  {/* Table Header */}
                  <div
                    style={{
                      display: "flex",
                      borderBottom: "1px solid var(--colorNeutralStroke1)",
                      backgroundColor: "var(--colorNeutralBackground2)",
                      fontWeight: 600,
                      padding: "12px 16px",
                    }}
                  >
                    <div style={{ width: "200px" }}>
                      {t("qtg.settings.cascoConditions.condition", "Condition")}
                    </div>
                    <div style={{ flex: 1 }}>
                      {t("qtg.settings.cascoConditions.conditionText")}
                    </div>
                  </div>
                  {/* Table Body */}
                  {insurerConditions.map((condition) => (
                    <div
                      key={condition.CascoConditionInsurerId}
                      style={{
                        display: "flex",
                        borderBottom: "1px solid var(--colorNeutralStroke1)",
                        padding: "12px 16px",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ width: "200px", paddingTop: "8px" }}>
                        <Text weight="semibold">{condition.ConditionName}</Text>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Textarea
                          value={
                            editingConditions[
                              condition.CascoConditionInsurerId
                            ] || ""
                          }
                          onChange={(_, data) =>
                            handleConditionTextChange(
                              condition.CascoConditionInsurerId,
                              data.value
                            )
                          }
                          placeholder={t(
                            "qtg.settings.cascoConditions.conditionTextPlaceholder"
                          )}
                          rows={3}
                          resize="vertical"
                          style={{ width: "100%" }}
                          disabled={!canEditSettings}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardPreview>
        </Card>
      </div>

      <ConfirmDialog
        open={showUnsavedChangesDialog}
        onClose={() => setShowUnsavedChangesDialog(false)}
        onConfirm={handleConfirmLeave}
        title={t("qtg.common.unsavedChanges", "Unsaved Changes")}
        message={t(
          "qtg.settings.cascoConditions.confirmUnsavedChanges",
          "You have unsaved changes. Are you sure you want to leave?"
        )}
        confirmText={t("qtg.common.leave", "Leave")}
        cancelText={t("qtg.common.stay", "Stay")}
      />
    </div>
  );
};
